/**
 * <s-pdp-gallery>: product page photos.
 *
 * Nov 3 (merchant): ONE continuous run through every color, in the order the colors are listed in the
 * picker: each color's main photo, then its other views (back, close-up), then straight on into the next
 * color. Untagged photos (the size chart) come once at the very end.
 * - As the photos move into another color, that color becomes the selected color in the picker
 *   (price, variant and Add to cart follow), so shoppers always know which color they're looking at.
 * - Picking a color in the picker jumps straight to that color, on the same view (front/back/close-up).
 * - Arrows and swipes; past the last photo the arrows go round to the first.
 *
 * Markup contract (templates/product.liquid):
 *   data-color on the element      — the color selected on load
 *   [data-pdp-track]               — the scroller holding the slides
 *   [data-pdp-slide][data-color]   — one per photo; untagged photos belong to every color
 *   [data-pdp-nav], [data-pdp-prev], [data-pdp-next], [data-pdp-dots]
 */
class PdpGallery extends HTMLElement {
  #ac = null;
  #color = '';
  #visible = [];
  #selfChange = false;
  #settleTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-pdp-track]');
    this.nav = this.querySelector('[data-pdp-nav]');
    this.dots = this.querySelector('[data-pdp-dots]');
    this.#color = this.dataset.color || '';
    if (!this.track) return;

    this.#order();

    this.querySelector('[data-pdp-prev]')?.addEventListener('click', () => this.#go(this.#index() - 1), opts);
    this.querySelector('[data-pdp-next]')?.addEventListener('click', () => this.#go(this.#index() + 1), opts);
    this.track.addEventListener('scroll', () => {
      this.#paintDots();
      this.dataset.view = String(this.#index() + 1);
      // Nov 3 (merchant): the selected color changes the instant the next color's first photo arrives, not after the swipe.
      this.#followColor();
    }, { ...opts, passive: true });

    // The variant picker is outside this element; a color the shopper picks there jumps the photos to it.
    document.addEventListener('change', (event) => {
      if (this.#selfChange) return;
      const input = event.target;
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
      if (!input.closest('s-variant-picker')) return;
      if (!this.#isColorInput(input)) return;
      const view = this.#viewInColor();
      this.#color = input.value;
      this.#jumpToColor(this.#color, view);
    }, opts);

    // Nov 2: a shared link (?photo=N) opens on the exact photo the sender was looking at.
    const shared = parseInt(new URLSearchParams(location.search).get('photo') || '', 10);
    if (shared > 1) this.#scrollTo(Math.min(shared - 1, this.#visible.length - 1), 'instant');
    else this.#jumpToColor(this.#color, 0);
    this.dataset.view = String(this.#index() + 1);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#settleTimer);
  }

  #isColorInput(input) {
    const name = (input.name || '').toLowerCase();
    return name === 'color' || name === 'colour';
  }

  #colorInputs() {
    return [...document.querySelectorAll('s-variant-picker input, s-variant-picker select')].filter((el) => this.#isColorInput(el));
  }

  // Lay the slides out color by color, in the picker's order, with untagged photos once at the end.
  #order() {
    const slides = [...this.querySelectorAll('[data-pdp-slide]')];
    const order = [];
    this.#colorInputs().forEach((el) => {
      const values = el instanceof HTMLSelectElement ? [...el.options].map((o) => o.value) : [el.value];
      values.forEach((v) => { if (v && !order.includes(v)) order.push(v); });
    });
    slides.forEach((s) => { const c = s.dataset.color; if (c && !order.includes(c)) order.push(c); });
    const ordered = [];
    order.forEach((color) => slides.forEach((s) => { if (s.dataset.color === color) ordered.push(s); }));
    slides.forEach((s) => { if (!s.dataset.color) ordered.push(s); });
    ordered.forEach((s) => { s.hidden = false; this.track.append(s); });
    this.#visible = ordered;

    const many = ordered.length > 1;
    if (this.nav) this.nav.hidden = !many;
    if (this.dots) {
      const width = ordered.length > 8 ? 'w-2' : 'w-5';
      this.dots.replaceChildren(...ordered.map(() => {
        const dot = document.createElement('span');
        dot.className = `block h-px ${width} bg-on-surface/25 transition-colors`;
        return dot;
      }));
    }
  }

  #firstOf(color) {
    return this.#visible.findIndex((s) => s.dataset.color === color);
  }

  // Which view (0 = front, 1 = back, ...) of the current color is showing.
  #viewInColor() {
    const index = this.#index();
    const color = this.#visible[index]?.dataset.color;
    const first = color ? this.#firstOf(color) : -1;
    return first >= 0 ? index - first : 0;
  }

  #jumpToColor(color, view) {
    const first = this.#firstOf(color);
    if (first < 0) return;
    const count = this.#visible.filter((s) => s.dataset.color === color).length;
    this.#scrollTo(first + Math.min(view, count - 1), 'instant');
  }

  // The moment the photos reach another color: the color button and the "Color: …" label switch at once;
  // price, variant and Add to cart catch up a beat later (one update, not one per photo passed mid-swipe).
  #followColor() {
    const color = this.#visible[this.#index()]?.dataset.color;
    if (!color || color === this.#color) return;
    this.#color = color;
    this.dataset.color = color;
    const input = this.#colorInputs().find((el) => (el instanceof HTMLSelectElement ? [...el.options].some((o) => o.value === color) : el.value === color));
    if (!input) return;
    if (input instanceof HTMLSelectElement) input.value = color;
    else input.checked = true;
    const legend = input.closest('fieldset')?.querySelector('legend');
    if (legend) {
      const name = legend.textContent.split(':')[0].trim();
      legend.textContent = `${name}: ${color}`;
    }
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      const live = this.#colorInputs().find((el) => (el instanceof HTMLSelectElement ? [...el.options].some((o) => o.value === this.#color) : el.value === this.#color));
      if (!live) return;
      if (live instanceof HTMLSelectElement) live.value = this.#color;
      else live.checked = true;
      this.#selfChange = true;
      try {
        live.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        this.#selfChange = false;
      }
    }, 120);
  }

  #index() {
    const width = this.track.clientWidth || 1;
    return Math.round(Math.abs(this.track.scrollLeft) / width);
  }

  #scrollTo(index, behavior) {
    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: dir * index * this.track.clientWidth, behavior });
    this.#paintDots();
  }

  #go(target) {
    const count = this.#visible.length;
    if (!count) return;
    this.#scrollTo(((target % count) + count) % count, 'smooth');
  }

  #paintDots() {
    if (!this.dots) return;
    const active = this.#index();
    [...this.dots.children].forEach((dot, i) => {
      dot.classList.toggle('bg-on-surface', i === active);
      dot.classList.toggle('bg-on-surface/25', i !== active);
    });
  }
}

if (!customElements.get('s-pdp-gallery')) customElements.define('s-pdp-gallery', PdpGallery);
